// RoomSound
// Features:
// - Wrapper for PositionalAudio
// - Manages play/stop state based on global sound setting and phase
// - Handles AudioContext resumption

import { useEffect, useRef } from 'react'
import { PositionalAudio } from '@react-three/drei'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'

import { useStore } from '../../store/useStore'
import { usePhase, PHASES } from '../../store/usePhase'

export default function RoomSound({ url, volume = 1.0, position = [0, 0, 0] }) {
    const audioRef = useRef()
    const isSoundOn = useStore((state) => state.isSoundOn)
    const phase = usePhase((state) => state.phase)
    const { camera } = useThree()

    // Only allow playback if sound is on AND we are past the loading screen
    const shouldPlay =
        isSoundOn && phase !== PHASES.loading && phase !== PHASES.warmup

    // Initialization & Volume management
    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        // Configure physics
        audio.setRefDistance(2.5)
        audio.setRolloffFactor(2.8)
        audio.setDistanceModel('exponential')
        audio.setLoop(true)

        // Set volume
        audio.setVolume(isSoundOn ? volume : 0)

        // Play/Pause logic
        if (shouldPlay && !audio.isPlaying) {
            // Ensure context is running
            const ctx = THREE.AudioContext.getContext()
            if (ctx && ctx.state !== 'running') ctx.resume()

            audio.play()
        } else if (!shouldPlay && audio.isPlaying) {
            audio.pause()
        }
    }, [shouldPlay, isSoundOn, volume])

    // Ensure AudioListener exists on camera
    useEffect(() => {
        if (!camera.children.some((c) => c.type === 'AudioListener')) {
            const listener = new THREE.AudioListener()
            camera.add(listener)
        }
    }, [camera])

    return (
        <PositionalAudio
            ref={audioRef}
            url={url}
            autoplay={false}
            loop
            position={position}
        />
    )
}
